import React, { useRef } from 'react';
import { Box, Container, Typography, Button, Chip, Stack } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LockIcon from '@mui/icons-material/Lock';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const FloatingShape = ({ style, delay = 0 }) => (
  <motion.div
    style={{ position: 'absolute', borderRadius: '50%', opacity: 0.06, ...style }}
    animate={{ y: [0, -30, 0], x: [0, 15, 0], scale: [1, 1.05, 1] }}
    transition={{ duration: 8 + delay, repeat: Infinity, ease: 'easeInOut', delay }}
  />
);

const StatBadge = ({ value, label }) => (
  <Box
    sx={{
      textAlign: 'center',
      p: 2,
      borderRadius: 3,
      bgcolor: 'background.paper',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(99,102,241,0.12)',
      minWidth: 100,
    }}
  >
    <Typography variant="h5" fontWeight={800} color="primary.main">{value}</Typography>
    <Typography variant="caption" color="text.secondary" fontWeight={500}>{label}</Typography>
  </Box>
);

export default function HeroSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollToTool = () => document.getElementById('tool')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <Box
      id="hero"
      ref={ref}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: (theme) => theme.palette.mode === 'dark'
          ? 'linear-gradient(160deg, #0d0d1f 0%, #0f0f1a 40%, #1a0d09 100%)'
          : 'linear-gradient(160deg, #f0f0ff 0%, #fafafa 40%, #fff7ed 100%)',
        pt: 10,
      }}
    >
      {/* Parallax background shapes */}
      <motion.div style={{ position: 'absolute', inset: 0, y }}>
        <FloatingShape style={{ width: 500, height: 500, background: '#6366f1', top: -100, right: -100 }} delay={0} />
        <FloatingShape style={{ width: 300, height: 300, background: '#f59e0b', bottom: 50, left: -50 }} delay={2} />
        <FloatingShape style={{ width: 200, height: 200, background: '#10b981', top: '40%', left: '30%' }} delay={4} />
        <FloatingShape style={{ width: 150, height: 150, background: '#6366f1', bottom: '20%', right: '20%' }} delay={1} />

        {/* Grid pattern */}
        <Box
          sx={{
            position: 'absolute', inset: 0,
            backgroundImage: 'radial-gradient(circle, rgba(99,102,241,0.06) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </motion.div>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div style={{ opacity }}>
          <Stack spacing={4} alignItems="center" textAlign="center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              <Chip
                icon={<LockIcon sx={{ fontSize: '0.85rem !important' }} />}
                label={t('hero.badge')}
                sx={{
                  background: 'rgba(99,102,241,0.1)',
                  color: 'primary.dark',
                  fontWeight: 600,
                  px: 1,
                  mb: 2,
                  border: '1px solid rgba(99,102,241,0.2)',
                }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3rem', sm: '4.5rem', md: '6rem' },
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #f1f5f9 0%, #818cf8 60%)'
                    : 'linear-gradient(135deg, #111827 0%, #6366f1 60%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('hero.title')}
                <br />
                <Box component="span" sx={{ color: 'primary.main', WebkitTextFillColor: 'initial' }}>
                  {t('hero.subtitle')}
                </Box>
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, fontWeight: 400, lineHeight: 1.7, fontSize: { xs: '1rem', md: '1.15rem' } }}
              >
                {t('hero.description')}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToTool}
                  endIcon={<ArrowDownwardIcon />}
                  sx={{ px: 4, py: 1.5, fontSize: '1rem' }}
                >
                  {t('hero.cta')}
                </Button>
              </Stack>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" sx={{ mt: 2 }}>
                <StatBadge value="100%" label="Free" />
                <StatBadge value="0" label="Stored" />
                <StatBadge value="18" label="Languages" />
                <StatBadge value="∞" label="Files" />
              </Stack>
            </motion.div>
          </Stack>
        </motion.div>
      </Container>

      {/* Scroll indicator */}
      <motion.div
        style={{ position: 'absolute', bottom: 32, left: '50%', x: '-50%' }}
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <Box sx={{ width: 26, height: 42, border: '2px solid rgba(99,102,241,0.3)', borderRadius: 13, display: 'flex', justifyContent: 'center', pt: 1 }}>
          <motion.div
            style={{ width: 4, height: 8, background: '#6366f1', borderRadius: 2 }}
            animate={{ y: [0, 14, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </Box>
      </motion.div>
    </Box>
  );
}
